import type { AccountId } from "@repo/app-types/entities/account";
import type { PlaybookId } from "@repo/app-types/entities/playbook";
import type { TeammateId } from "@repo/app-types/entities/teammate";
import type { GradingId } from "@repo/app-types/entities/teammatePerformanceSummary";
import { IconExternalLink } from "@tabler/icons-react";
import { createContext, type FC, type ReactNode, useContext } from "react";
import { twMerge } from "tailwind-merge";

/**
 * Identifies the grading a structured content reference points at. Mirrors the
 * props a host app needs to resolve and link to the underlying grading. Newer
 * content sets `gradingId`; older coaching content identifies the grading by
 * `teammateId`/`accountId`.
 */
export interface GradingReference {
  gradingId?: GradingId;
  teammateId?: TeammateId;
  accountId?: AccountId;
  playbookId: PlaybookId;
  criterionId?: string;
  target?: "assessment" | "transcript";
  /**
   * Verbatim quoted text, set for `quote` references. When present the host
   * links to the transcript tab and deep-links to the fragment via a `?quote=`
   * param, matching the research agent's quote links.
   */
  quote?: string;
}

export interface GradingReferenceWrapperProps {
  reference: GradingReference;
  /** Visual treatment shared by the resolved link and inert fallback. */
  className: string;
  children: ReactNode;
}

/**
 * An app-supplied component that makes a grading reference interactive —
 * typically wrapping it in a link to the referenced grading. Apps that can't
 * resolve gradings (e.g. the HQ playground) simply omit the provider, and
 * references render as inert styled text.
 */
export type GradingReferenceWrapper = FC<GradingReferenceWrapperProps>;

/**
 * Shared treatment for host-provided wrappers around grading references. The
 * focus utilities activate only when the wrapper resolves to an interactive
 * element; the marker lets the child affordance distinguish anchors from inert
 * fallback spans without requiring a second child tree.
 */
const gradingReferenceWrapperClassName =
  "grading-reference-wrapper box-decoration-clone rounded-md no-underline focus:outline-hidden focus-visible:ring-2 focus-visible:ring-indigo-600";

const GradingReferenceContext = createContext<
  GradingReferenceWrapper | null | undefined
>(undefined);

export const GradingReferenceProvider = GradingReferenceContext.Provider;

/**
 * Renders a grading reference from the AST. When the host app supplies an
 * interactive wrapper via `GradingReferenceProvider`, the wrapper can turn it
 * into a real link to the grading (mirroring the research agent's
 * quote/reference links, which navigate directly rather than opening a
 * popover). The external-link affordance is always in the child tree but only
 * displays under a resolved anchor. Without a provider it renders as inert
 * styled text, no affordance.
 * An explicitly null provider is a display-only nesting boundary: it preserves
 * the text while suppressing both interaction and reference-specific styling.
 *
 * Exists as a component so the plain-function AST renderer can consume the
 * context, and it owns the shared styling and affordance gating so the
 * external-link icon appears only when the reference is actually a link.
 */
interface GradingReferenceNodeProps {
  reference: GradingReference;
  /** Styling shared by interactive and inert references. */
  className: string;
  children: ReactNode;
}

export const GradingReferenceNode: FC<GradingReferenceNodeProps> = (props) => {
  const { reference, className, children } = props;
  const Wrapper = useContext(GradingReferenceContext);
  if (Wrapper === null) {
    return <>{children}</>;
  }
  if (!Wrapper) {
    return <span className={className}>{children}</span>;
  }

  // The outer wrapper owns the interaction. Null the provider for its subtree
  // so nested references render as plain content without overlapping UI.
  return (
    <Wrapper
      reference={reference}
      className={twMerge(
        gradingReferenceWrapperClassName,
        reference.quote === undefined
          ? "focus-visible:ring-offset-1"
          : "focus-visible:ring-offset-0",
        className,
      )}
    >
      <GradingReferenceProvider value={null}>
        <span className="whitespace-nowrap">
          <span className="whitespace-normal">{children}</span>
          <span className="hidden [a.grading-reference-wrapper_&]:inline">
            {"\u00a0"}
            <IconExternalLink className="-mt-0.5 inline size-3 shrink-0 align-middle" />
          </span>
        </span>
      </GradingReferenceProvider>
    </Wrapper>
  );
};
